import { useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import ArticleCard from '@/components/base/ArticleCard';
import { articles } from '@/mocks/articles';
import { categories } from '@/mocks/categories';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();

  const category = useMemo(() => categories.find((c) => c.slug === slug), [slug]);
  const categoryArticles = useMemo(
    () => articles.filter((a) => a.category === slug),
    [slug]
  );

  if (!category) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="min-h-screen flex items-center justify-center px-4">
          <div className="text-center">
            <h1 className="text-2xl font-heading text-foreground-900 mb-3">分类未找到</h1>
            <p className="text-foreground-600 mb-4">你查找的分类不存在或已被移除。</p>
            <Link to="/" className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-white rounded-full text-sm hover:bg-primary-600 transition-colors">
              <i className="ri-arrow-left-line"></i>
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const getIconColor = () => {
    if (category.color === 'accent') return 'bg-accent-100 text-accent-700';
    if (category.color === 'secondary') return 'bg-secondary-100 text-secondary-700';
    return 'bg-primary-100 text-primary-700';
  };

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main>
        <Breadcrumb
          items={[
            { label: '首页', href: '/' },
            { label: category.name },
          ]}
        />

        <section className="px-4 md:px-6 py-8 md:py-12 bg-background-50">
          <div className="max-w-6xl mx-auto">
            <div className="flex items-center gap-4 mb-6">
              <div className={`w-14 h-14 rounded-lg flex items-center justify-center ${getIconColor()}`}>
                <i className={`${category.icon} text-2xl`}></i>
              </div>
              <div>
                <h1 className="text-2xl md:text-3xl font-heading text-foreground-900 mb-1">
                  {category.name}
                </h1>
                <p className="text-sm text-foreground-600">
                  {category.description} · 共 {categoryArticles.length} 篇文章
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="px-4 md:px-6 py-8 md:py-12 bg-background-100">
          <div className="max-w-6xl mx-auto">
            {categoryArticles.length > 0 ? (
              <div className="space-y-4">
                {categoryArticles.map((article) => (
                  <ArticleCard key={article.slug} article={article} variant="default" />
                ))}
              </div>
            ) : (
              <div className="text-center py-16">
                <p className="text-foreground-600">该分类下暂无文章。</p>
              </div>
            )}
          </div>
        </section>

        <section className="px-4 md:px-6 py-10 bg-background-50">
          <div className="max-w-6xl mx-auto">
            <div className="flex flex-wrap gap-2 justify-center">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`px-4 py-2 rounded-full text-sm transition-colors whitespace-nowrap ${
                    cat.slug === slug
                      ? 'bg-primary-500 text-white'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200 border border-background-200/70'
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            name: `${category.name} - 约炮攻略`,
            description: category.description,
            url: `https://pairfinder.github.io/category/${category.slug}`,
            isPartOf: {
              '@type': 'WebSite',
              name: '约炮攻略',
              url: 'https://pairfinder.github.io/',
            },
          }),
        }}
      />
    </>
  );
}